import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { getWorkOrders } from '../lib/workOrders'
import { getVehicles } from '../lib/supabase'
import { format, formatDistanceToNow } from 'date-fns'
import {
  Clock,
  CheckCircle,
  Loader,
  XCircle,
  ChevronDown,
  ChevronUp,
  Wrench,
  Truck,
  Car,
  Star,
  FileText,
  Phone,
  AlertCircle,
  Inbox
} from 'lucide-react'

// Status configuration
const STATUS_CONFIG = {
  pending: {
    label: 'Submitted',
    color: 'text-amber-700 bg-amber-50 border-amber-200',
    icon: Clock,
    description: 'Your request has been received'
  },
  in_progress: {
    label: 'In Progress',
    color: 'text-blue-700 bg-blue-50 border-blue-200',
    icon: Loader,
    description: 'Our team is working on this'
  },
  completed: {
    label: 'Completed',
    color: 'text-green-700 bg-green-50 border-green-200',
    icon: CheckCircle,
    description: 'This request has been fulfilled'
  },
  cancelled: {
    label: 'Cancelled',
    color: 'text-gray-500 bg-gray-50 border-gray-200',
    icon: XCircle,
    description: 'This request was cancelled'
  }
}

// Type icons
const TYPE_ICONS = {
  service_inquiry: Wrench,
  transport: Truck,
  pickup_delivery: Car,
  maintenance: Wrench,
  storage: Car,
  specialist: Star,
  other: FileText
}

// Priority dots
const PRIORITY_COLORS = {
  urgent: 'bg-red-500',
  high: 'bg-orange-500',
  normal: 'bg-blue-500',
  low: 'bg-gray-400'
}

// Single request card
const RequestCard = ({ request, vehicles }) => {
  const [expanded, setExpanded] = useState(false)
  const status = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending
  const StatusIcon = status.icon
  const TypeIcon = TYPE_ICONS[request.type] || FileText
  const vehicle = vehicles.find(v => v.id === request.vehicle_id)

  return (
    <div className={`bg-white border rounded-xl overflow-hidden transition-all ${
      expanded ? 'shadow-sm' : ''
    }`}>
      {/* Main row */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left p-4 flex items-start gap-4 hover:bg-gray-50/50 transition-colors"
      >
        {/* Status icon */}
        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 border ${status.color}`}>
          <StatusIcon size={18} className={request.status === 'in_progress' ? 'animate-spin' : ''} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold text-black">{request.title}</h3>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium ${status.color}`}>
                  {status.label}
                </span>
                {request.priority && request.priority !== 'normal' && (
                  <span className="flex items-center gap-1 text-xs text-rdc-taupe">
                    <span className={`w-1.5 h-1.5 rounded-full ${PRIORITY_COLORS[request.priority] || PRIORITY_COLORS.normal}`} />
                    {request.priority}
                  </span>
                )}
                {vehicle && (
                  <span className="text-xs text-rdc-taupe">
                    {vehicle.year} {vehicle.make} {vehicle.model}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xs text-rdc-taupe">
                {formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}
              </span>
              {expanded ? <ChevronUp size={16} className="text-rdc-taupe" /> : <ChevronDown size={16} className="text-rdc-taupe" />}
            </div>
          </div>
        </div>
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-4 pt-0 border-t border-gray-50">
          {/* Progress timeline */}
          <div className="ml-5 pl-9 space-y-4 py-4">
            {/* Step 1: Submitted */}
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <CheckCircle size={14} className="text-green-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-black">Request Submitted</p>
                <p className="text-xs text-rdc-taupe">
                  {format(new Date(request.created_at), 'MMM d, yyyy · h:mm a')}
                </p>
              </div>
            </div>

            {/* Step 2: In Progress */}
            <div className="flex items-start gap-3">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                request.status === 'in_progress' || request.status === 'completed'
                  ? 'bg-green-100' : 'bg-gray-100'
              }`}>
                {request.status === 'in_progress' ? (
                  <Loader size={14} className="text-blue-600 animate-spin" />
                ) : request.status === 'completed' ? (
                  <CheckCircle size={14} className="text-green-600" />
                ) : (
                  <Clock size={14} className="text-gray-400" />
                )}
              </div>
              <div>
                <p className={`text-xs font-medium ${
                  request.status === 'in_progress' || request.status === 'completed' ? 'text-black' : 'text-gray-400'
                }`}>
                  Being Handled
                </p>
                {request.status === 'in_progress' && (
                  <p className="text-xs text-blue-600">Our team is actively working on this</p>
                )}
              </div>
            </div>

            {/* Step 3: Completed */}
            <div className="flex items-start gap-3">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                request.status === 'completed' ? 'bg-green-100' : 'bg-gray-100'
              }`}>
                {request.status === 'completed' ? (
                  <CheckCircle size={14} className="text-green-600" />
                ) : (
                  <CheckCircle size={14} className="text-gray-300" />
                )}
              </div>
              <div>
                <p className={`text-xs font-medium ${
                  request.status === 'completed' ? 'text-black' : 'text-gray-400'
                }`}>
                  Completed
                </p>
                {request.status === 'completed' && request.updated_at && (
                  <p className="text-xs text-rdc-taupe">
                    {format(new Date(request.updated_at), 'MMM d, yyyy · h:mm a')}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Description */}
          {request.description && (
            <div className="ml-5 pl-9 pb-3">
              <p className="text-sm text-rdc-charcoal bg-gray-50 rounded-lg p-3">{request.description}</p>
            </div>
          )}

          {/* Provider info */}
          {request.provider_name && (
            <div className="ml-5 pl-9 pb-3">
              <div className="flex items-center gap-2 text-sm">
                <Star size={14} className="text-rdc-taupe" />
                <span className="text-rdc-taupe">Assigned to:</span>
                <span className="font-medium text-black">{request.provider_name}</span>
                {request.provider_phone && (
                  <a href={`tel:${request.provider_phone}`} className="flex items-center gap-1 text-rdc-primary hover:underline">
                    <Phone size={12} />
                    {request.provider_phone}
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {request.notes && (
            <div className="ml-5 pl-9 pb-2">
              <p className="text-xs text-rdc-taupe"><span className="font-medium">Notes:</span> {request.notes}</p>
            </div>
          )}

          {/* Reference */}
          <div className="ml-5 pl-9 pt-2 border-t border-gray-100">
            <span className="text-xs text-rdc-taupe">
              Ref: #{request.id?.slice(0, 8).toUpperCase()}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

// Empty state
const EmptyRequests = () => (
  <div className="flex flex-col items-center justify-center py-16 px-8 text-center">
    <div className="w-16 h-16 rounded-full bg-rdc-primary/10 flex items-center justify-center mb-4">
      <Inbox size={28} className="text-rdc-primary" />
    </div>
    <h3 className="font-display text-lg font-semibold text-black mb-2">No Requests Yet</h3>
    <p className="text-sm text-rdc-taupe max-w-sm">
      When you ask the concierge to schedule service, arrange transport, or find a specialist,
      your requests will appear here so you can track their progress.
    </p>
  </div>
)

// Filter tabs
const FILTER_TABS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'completed', label: 'Completed' }
]

// Main component
export default function ConciergeRequests() {
  const { user } = useAuth()
  const [requests, setRequests] = useState([])
  const [vehicles, setVehicles] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all')

  useEffect(() => {
    loadData()
  }, [user])

  const loadData = async () => {
    if (!user?.id) return
    setLoading(true)

    try {
      const [workOrdersRes, vehiclesRes] = await Promise.all([
        getWorkOrders(user.id, { limit: 50 }),
        getVehicles(user.id)
      ])

      if (workOrdersRes.data) {
        setRequests(workOrdersRes.data)
      }
      if (!vehiclesRes.error && vehiclesRes.data) {
        setVehicles(vehiclesRes.data)
      }
    } catch (err) {
      console.error('Error loading requests:', err)
    } finally {
      setLoading(false)
    }
  }

  // Apply filter
  const filteredRequests = requests.filter(r => {
    if (filter === 'active') return r.status === 'pending' || r.status === 'in_progress'
    if (filter === 'completed') return r.status === 'completed' || r.status === 'cancelled'
    return true
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-rdc-primary border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Filter bar */}
      <div className="bg-white rounded-xl border border-rdc-warm-gray p-3 mb-4 flex items-center gap-2">
        {FILTER_TABS.map(tab => {
          const count = tab.key === 'all' ? requests.length
            : tab.key === 'active' ? requests.filter(r => r.status === 'pending' || r.status === 'in_progress').length
            : requests.filter(r => r.status === 'completed' || r.status === 'cancelled').length

          return (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === tab.key
                  ? 'bg-rdc-primary text-white'
                  : 'text-rdc-taupe hover:bg-gray-100'
              }`}
            >
              {tab.label}
              {count > 0 && (
                <span className={`ml-1.5 text-xs ${
                  filter === tab.key ? 'text-white/80' : 'text-rdc-taupe'
                }`}>
                  ({count})
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Requests list */}
      <div className="flex-1 overflow-y-auto space-y-3">
        {filteredRequests.length === 0 ? (
          <EmptyRequests />
        ) : (
          filteredRequests.map(request => (
            <RequestCard
              key={request.id}
              request={request}
              vehicles={vehicles}
            />
          ))
        )}
      </div>
    </div>
  )
}
